import { Vector3 } from 'three'
import type { Bus } from '../game/bus'
import type { BusSplineFollower } from '../game/busSplineFollower'
import { convertPosition } from '../core/unity'
import type { BusFleet } from './busView'

/**
 * Records how a bus's body behaves over the parked-to-road handover, so what
 * the eye reports as "not quite right" lands as numbers.
 *
 * The claim being checked is that the nose pulls away while the tail holds
 * station — `BusSplineFollower.LateUpdate` leaves `_tailPercent` where
 * `Initialize` put it until `_isFullyGrown`, so the body should grow out of the
 * entry rather than slide out of it. This samples both ends per frame and
 * reports how far each actually moved, alongside the drawn length and the state
 * of the lids and rings.
 *
 * Turned on with `?trace=body` in the URL, or `busBodyTrace(true)` from the
 * console. Each bus reports once, a few frames after it finishes growing.
 */

export interface BodySample {
  /** Seconds since the bus took a curve. */
  t: number
  phase: 'entry' | 'road'
  grown: boolean
  /** Arc distance of the nose along whichever curve the bus is on. */
  head: number
  /** Current head-to-tail span; short while the body is still emerging. */
  span: number
  /** How far the nose moved since the previous frame, world units. */
  noseStep: number
  /** How far the tail moved since the previous frame, world units. */
  tailStep: number
  /** How much of the tail cap has cleared the start of the curve. */
  tailCap: number
  /** `capFront + span + tailCap` — the whole drawn extent. */
  drawn: number
  ringsVisible: number
  lidsVisible: number
  /** Peak `Anim` blend shape weight across the rings, 0-200. */
  morph: number
}

let enabled = false

export function busBodyTrace(on = true): void {
  enabled = on
  console.log(`[body] tracing ${on ? 'on' : 'off'}`)
}

// Deliberately its own flag: `?trace` alone is the merge trace, and turning
// both on at once buries one table under the other.
if (typeof location !== 'undefined' && location.search.includes('trace=body')) enabled = true
if (typeof window !== 'undefined') {
  ;(window as unknown as { busBodyTrace: typeof busBodyTrace }).busBodyTrace = busBodyTrace
}

const _nose = new Vector3()
const _tail = new Vector3()

export class BodyTrace {
  private readonly samples = new Map<number, BodySample[]>()
  private readonly last = new Map<number, { nose: Vector3; tail: Vector3 }>()
  private readonly elapsed = new Map<number, number>()
  private readonly reported = new Set<number>()
  private readonly grownAt = new Map<number, number>()

  get on(): boolean {
    return enabled
  }

  /** Call once per frame per bus that has a follower, after the pose. */
  sample(bus: Bus, follower: BusSplineFollower, fleet: BusFleet | null, dt: number): void {
    if (!enabled) return

    const span = follower.grownBodyLength
    const info = fleet?.debugInfo(bus) ?? null
    const capFront = info?.capFront ?? 0
    const capBack = info?.capBack ?? 0
    // The **drawn** ends, not the head/tail points: the nose cap runs `capFront`
    // ahead of the head, and the tail cap only as far back as it has room for.
    // Those are the same numbers `poseBent` maps the body through.
    const room = follower.tailCapRoom(capBack)
    convertPosition(follower.bodyPose(-capFront).position, _nose)
    convertPosition(follower.bodyPose(span + room).position, _tail)

    const t = (this.elapsed.get(bus.id) ?? 0) + dt
    this.elapsed.set(bus.id, t)

    const previous = this.last.get(bus.id)
    if (previous) {
      let rows = this.samples.get(bus.id)
      if (!rows) {
        rows = []
        this.samples.set(bus.id, rows)
      }
      const flat = (a: Vector3, b: Vector3) => Math.hypot(a.x - b.x, a.z - b.z)
      rows.push({
        t,
        phase: follower.isOnEntryPath ? 'entry' : 'road',
        grown: follower.isFullyGrown,
        head: follower.headDistance,
        span,
        noseStep: flat(_nose, previous.nose),
        tailStep: flat(_tail, previous.tail),
        tailCap: room,
        drawn: capFront + span + room,
        ringsVisible: info?.ringsVisible ?? -1,
        lidsVisible: info?.lidsVisible ?? -1,
        morph: info?.morph ?? -1,
      })
    }

    this.last.set(bus.id, { nose: _nose.clone(), tail: _tail.clone() })

    // The moment that matters is the **tail cap** filling out, not
    // `_isFullyGrown` — that flips on the first frame, because `Initialize`
    // places the bus already formed.
    if (room >= capBack - 1e-3 && !this.grownAt.has(bus.id)) {
      this.grownAt.set(bus.id, this.samples.get(bus.id)?.length ?? 0)
    }
    // Let it run a little past that so the settled numbers are in view.
    const grew = this.grownAt.get(bus.id)
    const count = this.samples.get(bus.id)?.length ?? 0
    if (grew !== undefined && count > grew + 40 && !this.reported.has(bus.id)) {
      this.reported.add(bus.id)
      this.report(bus)
    }
  }

  private report(bus: Bus): void {
    const rows = this.samples.get(bus.id) ?? []
    if (rows.length === 0) return

    const grew = this.grownAt.get(bus.id) ?? rows.length
    const growing = rows.slice(0, grew)
    let noseTravel = 0
    let tailTravel = 0
    for (const r of growing) {
      noseTravel += r.noseStep
      tailTravel += r.tailStep
    }
    const ratio = tailTravel > 0.001 ? (noseTravel / tailTravel).toFixed(1) : 'inf'

    console.groupCollapsed(
      `[body] bus #${bus.id} size ${bus.size} — lengthened over ${grew} frames` +
        ` (${(growing[growing.length - 1]?.t ?? 0).toFixed(2)}s), drawn ` +
        `${(growing[0]?.drawn ?? 0).toFixed(2)} -> ${(rows[rows.length - 1]?.drawn ?? 0).toFixed(2)},` +
        ` nose ${noseTravel.toFixed(2)}u vs tail ${tailTravel.toFixed(2)}u (${ratio}x)`,
    )
    console.log(
      'body length', bus.endPointSpan.toFixed(2),
      '| the middle should be that from row 0 —',
      'what opens up is `tailCap`, clipped against the start of the entry curve',
    )
    console.table(
      rows.map((r) => ({
        t: +r.t.toFixed(3),
        phase: r.phase,
        grown: r.grown,
        head: +r.head.toFixed(2),
        span: +r.span.toFixed(2),
        tailCap: +r.tailCap.toFixed(2),
        drawn: +r.drawn.toFixed(2),
        nose: +r.noseStep.toFixed(3),
        tail: +r.tailStep.toFixed(3),
        rings: r.ringsVisible,
        lids: r.lidsVisible,
        morph: +r.morph.toFixed(0),
      })),
    )
    console.groupEnd()
  }

  reset(): void {
    this.samples.clear()
    this.last.clear()
    this.elapsed.clear()
    this.reported.clear()
    this.grownAt.clear()
  }
}
